import {
  Box,
  Button,
  FormControl,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import LensIcon from '@material-ui/icons/Lens';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Canceler } from 'axios';
import copy from 'clipboard-copy';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RoundedButton } from '../../../components/RoundedButton';
import api from '../../../services/api/base';
import { deactiveRedeemCodeById, getRedeemCodes } from '../../../services/api/RedeemCode';
import { IRedeemCode } from '../../../services/types';
import CreateRedeemCodeCard from './CreateReedemCodeCard';
import useStyles from './styles';
import useConfirmDialog from '../../../hooks/useConfirmDialog';

interface IProps {}

const RedeemCode = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const [redeemCodes, setRedeemCodes] = useState<IRedeemCode[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [showCreateCard, setShowCreateCard] = useState<boolean>(false);
  const [showEditCard, setShowEditCard] = useState<boolean>(false);
  const [filter, setFilter] = useState<'all' | 'active' | 'deactive'>('all');
  const [page, setPage] = useState<number>(1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRedeemCode, setSelectedRedeemCode] = useState<IRedeemCode | null>(null);
  const [open, setOpen] = React.useState(false);
  const confirm = useConfirmDialog();

  const canceler = useRef<Canceler>(() => undefined);

  const handleClose = (event: any, reason: any) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const fetchData = useCallback(
    async (filter: 'all' | 'active' | 'deactive' = 'all') => {
      canceler.current();
      try {
        const [fetchPromise, cancel] = getRedeemCodes(filter, page);
        canceler.current = cancel;
        const result = await fetchPromise;

        if (page > 1) {
          setRedeemCodes(ps => [...ps].concat(result.data.redeemCodes));
        } else {
          setRedeemCodes(result.data.redeemCodes);
        }

        setHasMore(result.data.hasMore);
      } catch (e) {
        if (api.isCancel(e)) return;
      }
    },
    [page],
  );

  const openMenu = (event: any, redeemCode: IRedeemCode) => {
    setAnchorEl(event.currentTarget);
    setSelectedRedeemCode(redeemCode);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedRedeemCode(null);
  };

  const deactiveRedeemCode = async () => {
    if (selectedRedeemCode) {
      try {
        await deactiveRedeemCodeById(selectedRedeemCode.id);
        if (page !== 1) {
          setPage(1);
        } else {
          fetchData(filter);
        }
      } catch (e) {
        alert('Something went wrong!');
      }
    }
    setAnchorEl(null);
    setSelectedRedeemCode(null);
  };

  const deactivateCoupon = () => {
    confirm.open({
      text: 'You are about to deactivate this coupon.',
      onProceed: () => deactiveRedeemCode(),
    });
  };

  useEffect(() => {
    if (showCreateCard === false && showEditCard === false) {
      fetchData(filter);
    }

    return () => canceler.current();
  }, [page, showCreateCard, showEditCard, fetchData, filter]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  return (
    <div className={classes.root}>
      <RoundedButton
        Icon={<AddIcon color="primary" />}
        label={'Create Coupon'}
        onClick={() => {
          setShowCreateCard(true);
        }}
      />
      <Box className={classes.tableTitle} alignItems="center">
        <Typography variant="h6">Created Coupon List</Typography>
        <FormControl className={classes.listFilterFormControl}>
          <Select
            value={filter}
            onChange={e => {
              if (['all', 'active', 'deactive'].indexOf(e.target.value as string) !== -1) {
                fetchData(e.target.value as 'all' | 'active' | 'deactive');
                setFilter(e.target.value as 'all' | 'active' | 'deactive');
              }
            }}
            inputProps={{
              id: 'filter',
            }}
          >
            <MenuItem value={'all'}> All</MenuItem>
            <MenuItem value={'active'}> Active</MenuItem>
            <MenuItem value={'deactive'}> Deactivated</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Paper style={{ width: '100%' }}>
        <Table className={classes.redeemCodeTable}>
          <TableHead>
            <TableRow>
              <TableCell style={{ paddingRight: '8px', width: '36px' }}>Status</TableCell>
              <TableCell style={{ paddingLeft: '8px' }}>Title</TableCell>
              <TableCell>Redeem Code</TableCell>
              <TableCell>Expiration Date</TableCell>
              <TableCell align="right">Validity Period (Days)</TableCell>
              <TableCell align="right">Redeemable Code Left</TableCell>
              <TableCell align="right">Number of Coupons</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {redeemCodes.map(redeemCode => (
              <TableRow key={redeemCode.id}>
                <TableCell align="center">
                  <Tooltip title={redeemCode.isValid ? 'Activated' : 'Deactivated'}>
                    <LensIcon className={classes.statusIcon + ' ' + (redeemCode.isValid ? classes.statusValid : classes.statusInvalid)}></LensIcon>
                  </Tooltip>
                </TableCell>
                <TableCell style={{ paddingLeft: '8px' }}>
                  {redeemCode?.title}
                  {redeemCode.story && (
                    <Typography className={classes.storyDescription}>
                      ID: {redeemCode.story.id} Story: {redeemCode.story.title}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{redeemCode.code}</TableCell>
                <TableCell>{moment(redeemCode.expiredAt).format('ddd, MMM D, YYYY')}</TableCell>
                <TableCell align="right">{redeemCode.validityPeriod}</TableCell>
                <TableCell align="right">{redeemCode.count?.toLocaleString() || 0}</TableCell>
                <TableCell align="right">{redeemCode.amount?.toLocaleString() || 0}</TableCell>
                <TableCell>
                  <MoreVertIcon onClick={e => openMenu(e, redeemCode)} style={{ color: '#888888' }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={closeMenu}>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setShowEditCard(true);
            }}
          >
            Edit
          </MenuItem>
          <MenuItem onClick={deactivateCoupon} disabled={selectedRedeemCode?.isValid ? false : true}>
            Deactivate
          </MenuItem>
          <MenuItem
            onClick={() => {
              const url = selectedRedeemCode?.redeemUrl;
              copy(url as string);
              setOpen(true);
            }}
          >
            Copy Url
          </MenuItem>
        </Menu>
      </Paper>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={open && selectedRedeemCode !== null}
        autoHideDuration={6000}
        onClose={handleClose}
        ContentProps={{
          'aria-describedby': 'message-id',
        }}
        message={selectedRedeemCode && <span id="message-id">{selectedRedeemCode.redeemUrl} is copied to clipboard</span>}
      />
      {showEditCard && (
        <CreateRedeemCodeCard
          closeCardFunction={setShowEditCard}
          isEdit={true}
          redeemCode={selectedRedeemCode ? selectedRedeemCode : undefined}
          successCallback={() => {
            setShowEditCard(false);
            setPage(1);
          }}
        />
      )}
      {showCreateCard && (
        <CreateRedeemCodeCard
          closeCardFunction={setShowCreateCard}
          isEdit={false}
          successCallback={() => {
            setShowCreateCard(false);
            setPage(1);
          }}
        />
      )}
      {hasMore && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Button
            className={classes.hasMoreButton}
            onClick={() => {
              setPage(page + 1);
            }}
          >
            Load more
          </Button>
        </Box>
      )}
    </div>
  );
};

export default RedeemCode;
